// Project category catalogue.
//
// Three top-level groups (Biography / Fiction / Non-Fiction) plus a
// richer set of narrative-format and genre sub-categories under
// each. Each entry carries:
//
//   value      — canonical string persisted to the BE's StoryDetails.
//                ``category`` CharField. Existing rows wrote one of
//                "Biography" | "Fiction" | "Non-Fiction"; the new
//                sub-categories extend that vocabulary without
//                breaking the legacy values.
//   labelKey   — i18n key resolved via useT().
//   group      — "biography" | "fiction" | "non_fiction". The catalog
//                browse parent filters use this to roll up sub-genre
//                projects under their parent (so a Fantasy project
//                still appears under the Fiction filter).
//   popularity — 0-100 score used to sort the searchable dropdown.
//                Hand-picked from a rough industry ranking so the
//                top of the list is "what writers usually want"
//                (Novel, Fantasy, Romance) rather than alphabetical
//                ("Autobiography" first). Higher = surfaces sooner.
//
// Adding a category: append here, add the EN/FR i18n keys, and the
// SearchableSelect + catalog filter pick it up with no other
// changes.

export const CATEGORY_GROUPS = {
  biography: "biography",
  fiction: "fiction",
  non_fiction: "non_fiction",
};

export const CATEGORY_OPTIONS = [
  // --- Parent buckets (existing 3, preserved for backwards
  //     compatibility with legacy rows + the catalog's parent
  //     filter chips). Kept at very high popularity so they sort
  //     above their own sub-genres in the dropdown.
  { value: "Fiction",       labelKey: "create_project.category.fiction",       group: "fiction",     popularity: 95 },
  { value: "Non-Fiction",   labelKey: "create_project.category.non_fiction",   group: "non_fiction", popularity: 85 },
  { value: "Biography",     labelKey: "create_project.category.biography",     group: "biography",   popularity: 75 },

  // --- Fiction sub-genres
  { value: "Novel",             labelKey: "create_project.category.novel",             group: "fiction", popularity: 100 },
  { value: "Fantasy",           labelKey: "create_project.category.fantasy",           group: "fiction", popularity: 92 },
  { value: "Romance",           labelKey: "create_project.category.romance",           group: "fiction", popularity: 90 },
  { value: "Mystery",           labelKey: "create_project.category.mystery",           group: "fiction", popularity: 86 },
  { value: "Thriller",          labelKey: "create_project.category.thriller",          group: "fiction", popularity: 84 },
  { value: "Science Fiction",   labelKey: "create_project.category.science_fiction",   group: "fiction", popularity: 82 },
  { value: "Historical Fiction", labelKey: "create_project.category.historical_fiction", group: "fiction", popularity: 76 },
  { value: "Young Adult",       labelKey: "create_project.category.young_adult",       group: "fiction", popularity: 75 },
  { value: "Horror",            labelKey: "create_project.category.horror",            group: "fiction", popularity: 70 },
  { value: "Children's Book",   labelKey: "create_project.category.childrens",         group: "fiction", popularity: 68 },
  { value: "Short Story",       labelKey: "create_project.category.short_story",       group: "fiction", popularity: 65 },
  { value: "Poetry",            labelKey: "create_project.category.poetry",            group: "fiction", popularity: 60 },
  { value: "Graphic Novel",     labelKey: "create_project.category.graphic_novel",     group: "fiction", popularity: 56 },
  { value: "Drama",             labelKey: "create_project.category.drama",             group: "fiction", popularity: 50 },
  { value: "Screenplay",        labelKey: "create_project.category.screenplay",        group: "fiction", popularity: 44 },

  // --- Biography sub-genres
  { value: "Memoir",            labelKey: "create_project.category.memoir",            group: "biography", popularity: 78 },
  { value: "Autobiography",     labelKey: "create_project.category.autobiography",     group: "biography", popularity: 60 },

  // --- Non-Fiction sub-genres
  { value: "Self-Help",         labelKey: "create_project.category.self_help",         group: "non_fiction", popularity: 80 },
  { value: "History",           labelKey: "create_project.category.history",           group: "non_fiction", popularity: 72 },
  { value: "Essay",             labelKey: "create_project.category.essay",             group: "non_fiction", popularity: 70 },
  { value: "Science",           labelKey: "create_project.category.science",           group: "non_fiction", popularity: 65 },
  { value: "Business",          labelKey: "create_project.category.business",          group: "non_fiction", popularity: 64 },
  { value: "Travel",            labelKey: "create_project.category.travel",            group: "non_fiction", popularity: 50 },
];

// Lookup helper — keyed by lowercase value so a stored "fantasy"
// resolves the same entry as "Fantasy". Used by CategoryShowcase's
// parent-filter rollup (Fiction picks up any entry whose group is
// "fiction") and by the SearchableSelect's "show selected label"
// path for stored values that may not match case exactly.
const _byValueLower = new Map(
  CATEGORY_OPTIONS.map((o) => [o.value.toLowerCase(), o]),
);

export function findCategoryByValue(value) {
  if (!value || typeof value !== "string") return null;
  return _byValueLower.get(value.toLowerCase()) || null;
}

// Returns the group ("biography" | "fiction" | "non_fiction") for a
// stored category value, falling back to null when it doesn't match
// any catalogue entry (legacy free-form text).
export function categoryGroupFor(value) {
  return findCategoryByValue(value)?.group || null;
}
